import type { SupWeb3SelectedRoute } from "@/components/Web3/Common/Route/State";
import type { Web3TxMultiCalcSWR } from "@/hooks/tx-flow/useDepositCalculate";
import type { ResponseTxFees } from "@/hooks/tx-flow/useDepositStart";
import type { SupSinglePreparedRoute } from "@/components/Web3/Common/Route/utils/prepareProviderRoutes";
import type { SupWeb3Address, SupWeb3TxSingleVault } from "@/types";
import { useMemo } from "react";

import { useWeb3DebugContext } from "@/components/Web3/Debug/State";
import useApi from "@/hooks/api/useApi";
import useGetRefundAddress from "@/hooks/web3/useGetRefundAddress";
import { useStoredData } from "@/store";

export interface ResponseWithdrawStart extends ResponseTxFees {
  to: SupWeb3Address;
  data: string;
  value: bigint;
  method: string;
  value_usd?: number;
  approval_data:
    | {
        to: SupWeb3Address;
        data: string;
        value: bigint;
      }
    | undefined
    | null;
}

/**
 * @package api:go
 * @link https://superform-xyz.postman.co/workspace/Superform~fe57d318-f198-4f97-9c50-0b2a5a78dbac/request/3472327-88c7e721-d25c-445b-ae1e-a6c0e0127b47?ctx=documentation
 */
export default function useWithdrawStart(vaultList: SupWeb3TxSingleVault[] | undefined, calculatedRoutes: Web3TxMultiCalcSWR, selectedRoutes: SupWeb3SelectedRoute[]) {
  const storedData = useStoredData();
  const { address: refundAddress } = useGetRefundAddress(storedData.withdrawalChainId);
  const { addStart } = useWeb3DebugContext();

  const preparedData = useMemo(() => {
    if (!vaultList) {
      addStart?.(undefined);
      return undefined;
    }

    try {
      const rawData = vaultList.map(vault => {
        const routeData = calculatedRoutes.data?.find(route => route.liq.vault_id === vault.id);
        const selectedRoute = selectedRoutes.find(single => single.vaultId === vault.id)?.selectedRoute as SupSinglePreparedRoute | "direct" | undefined;

        if (!routeData) {
          throw new Error("No route data");
        }
        if (!selectedRoute) {
          throw new Error("No selected route");
        }
        const foundRoute = typeof selectedRoute === "object" ? routeData.liq.routes.find(route => route.route_id === selectedRoute.id) : undefined;
        if (!foundRoute && typeof selectedRoute === "object") {
          throw new Error("No found route");
        }

        const inArg = {
          ...routeData.in,
          refund_address: refundAddress,
        };

        return {
          in: inArg,
          liq: {
            vault_id: vault.id,
            ok: true,
            routes: !foundRoute ? null : [foundRoute],
          },
        };
      });
      addStart?.(rawData);
      return rawData;
    } catch (e) {
      addStart?.(undefined);
      return undefined;
    }
  }, [addStart, calculatedRoutes.data, refundAddress, selectedRoutes, vaultList]);

  const { data, error, isLoading, isError, mutate } = useApi<ResponseWithdrawStart, ResponseWithdrawStart | "">(
    {
      path: "/withdraw/start/",
      api: "v2",
      data: preparedData,
      method: "POST",
      useJwt: true,
      forceJwt: false,
    },
    {
      enabled: Boolean(preparedData),
      strategy: "cached",
      select: data => {
        if (data === "") {
          throw new Error("API response is empty, please report this issue to the support team");
        }
        return data;
      },
    }
  );

  return {
    data,
    error,
    isLoading,
    isError,
    mutate,
  };
}
